import * as yup from 'yup';
import { buildValidator, buildComplexValidator } from './validation';
import moment from 'moment';
import { DATE_FORMAT } from './date';

/**
 * Enumeration of possible validation errors.
 * These are defined as keys so we can internationalize them.
 * Further, they are defined in a single object here so we can
 * display the list of errors for editing on an admin page.
 */

// TO DO - CHANGE THESE TO ACTUAL PHRASE APP KEYS IF THEY ARE DIFFERENT ONCE LOADED
export const ErrorMessage = {
  AMOUNT: 'validationAmountRequired',
  DATE_END_BEFORE_START: 'validationEndDateError',
  END_DATE_VALID: 'validationEndDateValid',
  END_DATE_BLANK: 'validationEndDateBlank',
  EXPENSE_TYPE: 'validationExpenseTypeRequired',
  FREQUENCY: 'validationFrequencyRequired',
  INCOME_TYPE: 'validationIncomeTypeRequired',
  MILLION_OR_LESS: 'validationMillionOrLess',
  MUST_BE_A_NUMBER: 'validationMustBeANumber',
  NEG_MIL_OR_MORE: 'validationNegMillionOrMore',
  START_DATE_VALID: 'validationStartDateValid',
  START_DATE_BLANK: 'validationStartDateBlank',
  ZERO_OR_MORE: 'validationNumberZeroOrMore',
  DATE_IN_SAME_YEAR: 'validationSameYears',
};

/*
 * Currency validator
 * Numbers only with no decimal or 2 decimal places
 */
const isNumericValue = (value) => {
  if (value !== null && value !== '') {
    return !isNaN(parseFloat(value));
  }
  return true;
};

/*
 * Income and expense amount validators
 */

const betweenNegMillionAndPosMillionSchema = yup
  .string()
  .trim()
  .test('isNumber', ErrorMessage.MUST_BE_A_NUMBER, isNumericValue)
  .test('more-than-neg-mil', ErrorMessage.NEG_MIL_OR_MORE, (value) => value >= -1000000)
  .test('less-than-pos-mil', ErrorMessage.MILLION_OR_LESS, (value) => value <= 1000000)
  .nullable()
  .required(ErrorMessage.AMOUNT);
const betweenNegMillionAndPosMillion = buildValidator(betweenNegMillionAndPosMillionSchema);

const between0andMillionSchema = yup
  .string()
  .trim()
  .test('isNumber', ErrorMessage.MUST_BE_A_NUMBER, isNumericValue)
  .test('0-or-more', ErrorMessage.ZERO_OR_MORE, (value) => value >= 0)
  .test('less-than-mil', ErrorMessage.MILLION_OR_LESS, (value) => value <= 1000000)
  .nullable()
  .required(ErrorMessage.AMOUNT);
const between0andMillion = buildValidator(between0andMillionSchema);

/*
 * Validator for required fields
 */
const requiredSchema = (errorMessage: string) =>
  yup
    .mixed()
    .test('notEmptyString', errorMessage, (value) => value !== '')
    .required(errorMessage);

const incomeTypeRequiredSchema = requiredSchema(ErrorMessage.INCOME_TYPE);
const incomeTypeRequired = buildValidator(incomeTypeRequiredSchema);

const expenseTypeRequiredSchema = requiredSchema(ErrorMessage.EXPENSE_TYPE);
const expenseTypeRequired = buildValidator(expenseTypeRequiredSchema);

const frequencyRequiredSchema = requiredSchema(ErrorMessage.FREQUENCY);
const frequencyRequired = buildValidator(frequencyRequiredSchema);

/*
 * Start date validator
 * Valid date in current year
 */
const hasYearMonthDay = (dateString) =>
  dateString &&
  dateString
    .split('-')
    .map((x) => parseInt(x, 10))
    .filter((x) => !isNaN(x) && x).length === 3;

const checkValidDate = (value) => {
  return !isNaN(Date.parse(value)) && moment(value, DATE_FORMAT).isValid() && hasYearMonthDay(value);
};

const checkBlankDate = (value) => {
  return value !== null && value.trim() !== '';
};

const validDate = (errorMessageValid: string, errorMessageBlank: string) => {
  return yup
    .string()
    .test('blankDate', errorMessageBlank, checkBlankDate)
    .test('invalidDate', errorMessageValid, checkValidDate)
    .transform((date) => {
      // Unfortunately yup actually runs all the transforms before the validations
      // regardless of where the function call is in the call chain, so we need to
      // do some checks here to make sure we don't accidentally turn a bad value
      // into a good one.
      if (checkValidDate(date)) {
        // Make sure the values are correctly zero-padded and such
        return moment(date, DATE_FORMAT).format(DATE_FORMAT);
      }
      return date;
    })
    .required(errorMessageBlank)
    .typeError(errorMessageValid);
};

/*
 * Start date validator
 * Valid date with the same year as end date
 */
const startDateSameYearAsEnd = (object: { value: string; endDate: string }) => {
  const { endDate } = object;
  if (!endDate || endDate.length === 0) {
    return null;
  } else {
    return yup.string().test('invalidTimeFrame', ErrorMessage.DATE_IN_SAME_YEAR, (value) => {
      const yearsAreTheSame = moment(value, DATE_FORMAT).year() === moment(endDate, DATE_FORMAT).year();
      return yearsAreTheSame;
    });
  }
};

const startDateCurrentYearSchema = validDate(ErrorMessage.START_DATE_VALID, ErrorMessage.START_DATE_BLANK);
const startDateInvalid = buildComplexValidator(startDateCurrentYearSchema, [startDateSameYearAsEnd]);

/*
 * End date validators
 * Valid date after start date
 */
const dateAfterStartDateSchema = (object) => {
  const { startDate } = object;
  return yup.string().test('invalidTimeFrame', ErrorMessage.DATE_END_BEFORE_START, (value) => {
    const checkingAgainstValidStartDate = startDate && checkValidDate(startDate);
    return !checkingAgainstValidStartDate || moment(value, DATE_FORMAT).isAfter(moment(startDate, DATE_FORMAT));
  });
};

/*
 * Valid date with the same year as the start date
 */
const endDateSameYearAsStart = (object) => {
  const { startDate } = object;
  return yup.string().test('invalidTimeFrame', ErrorMessage.DATE_IN_SAME_YEAR, (value) => {
    const checkingAgainstValidStartDate = startDate && checkValidDate(startDate);
    const yearsAreTheSame = moment(value, DATE_FORMAT).year() === moment(startDate, DATE_FORMAT).year();
    return !checkingAgainstValidStartDate || yearsAreTheSame;
  });
};

/**
 * Validates the date entered is valid, after the start date, and has the same year as the start date
 */
const endDateValid = validDate(ErrorMessage.END_DATE_VALID, ErrorMessage.END_DATE_BLANK);
const endDateInvalid = buildComplexValidator(endDateValid, [dateAfterStartDateSchema, endDateSameYearAsStart]);

/**
 * Collection of field-level validators that can be specified in questions.
 */
export const validators = {
  between0andMillion,
  betweenNegMillionAndPosMillion,
  endDateInvalid,
  expenseTypeRequired,
  frequencyRequired,
  incomeTypeRequired,
  startDateInvalid,
};
